import { Modal } from "antd";
import { makeAutoObservable } from "mobx";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { loadSpecification, ZSpecification } from "src/business/specification";
import { makeRowEvaluate } from "src/business/specification/rowEvaluate";
import {
  loadValueCatalog,
  valueCatalogCardsLoader,
} from "src/business/valueCatalog/apiValueCatalog";
import { buildValueCatalogColumns } from "src/business/valueCatalog/buildValueCatalogColumns";
import { ValueCatalogCardsFilter } from "src/business/valueCatalog/ValueCatalogFilter";
import { ZValueCatalog } from "src/business/valueCatalog/ZValueCatalog";
import { ZValueCatalogCardSafe } from "src/business/valueCatalog/ZValueCatalogCard";
import { onError } from "src/common/onError";
import { optionsMaker } from "src/common/optionsMaker";
import { RemoteData } from "src/common/RemoteData";
import { LoaderBox } from "src/components/LoaderBox";
import { MdmTable, MdmTableSystemTools } from "src/components/MdmTable";
import { RtSearch } from "src/components/ReactiveTable/components/RtSearch";
import { RtSelection } from "src/components/ReactiveTable/components/RtSelection";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import { RtKey } from "src/components/ReactiveTable/RtTypes";
import { SelectFromModalButton } from "src/components/SelectFromModalButton";
import { ToolBox2 } from "src/components/ToolBox2";
import { ZOption, ZSidLabel, zSidLabel } from "src/types";
import { ActivateCallback } from "src/types/ActivateCallback";

import { ZTypeDef } from "../../ZAttrMeta";
import { ZSelectValuesModal } from "../ZAttrComponent";
import * as styles from "./AttrSelectValuesModal.module.less";

type Data = {
  catalog: ZValueCatalog;
  spec: ZSpecification;
  tableStore: RtStore<ZValueCatalogCardSafe, ValueCatalogCardsFilter>;
  columns: RtColumn<ZValueCatalogCardSafe>[];
  makeOption: (key: RtKey, row?: ZValueCatalogCardSafe) => ZOption;
};

class SelectValueModalStore {
  constructor(
    public valuesCatalogId: string,
    public labelTemplate: string,
    public multi: boolean,
    srcValue: ZSidLabel[] | null,
  ) {
    makeAutoObservable(this);
    this.init(srcValue).catch(onError);
  }

  private async init(srcValue: ZSidLabel[] | null) {
    try {
      this.setData({ status: "wait" });
      const catalog = await loadValueCatalog(this.valuesCatalogId);
      const spec = await loadSpecification("values", catalog.schema.id);
      const tableStore = new RtStore<
        ZValueCatalogCardSafe,
        ValueCatalogCardsFilter
      >({
        rowKey: "id",
        loader: valueCatalogCardsLoader(this.valuesCatalogId, spec),
        selectionType: this.multi ? "checkbox" : "radio",
        keepSelected: true,
      });
      if (srcValue) {
        tableStore.setSelectedKeys(srcValue.map(({ id }) => id));
      }
      const columns = buildValueCatalogColumns(spec);
      const makeLabel = makeRowEvaluate(this.labelTemplate, [spec]);
      const makeOption = optionsMaker([], makeLabel);
      const result = { catalog, spec, tableStore, columns, makeOption };
      this.setData({ status: "ready", result });
    } catch (error) {
      this.setData({ status: "error", error });
    }
  }

  data: RemoteData<Data> = { status: "none" };

  setData(newData: RemoteData<Data>) {
    this.data = newData;
  }

  get title() {
    if (this.data.status !== "ready") return null;
    const { catalog } = this.data.result;
    return catalog.name;
  }
}

// Поддерживаются типы значений: strOption и Array<strOption>

type PropsAttrSelectValuesModal = {
  value?: unknown;
  onChange?(newValue?: unknown): void;
  attrType: ZTypeDef;
  component: ZSelectValuesModal;
  disabled?: boolean;
  activateCallback?: ActivateCallback;
  onBlur(): void;
};

export const AttrSelectValuesModal: React.FC<PropsAttrSelectValuesModal> =
  observer((props) => {
    const {
      disabled,
      activateCallback,
      value,
      onChange,
      component,
      attrType,
      onBlur,
    } = props;
    const multi = attrType.name === "array";
    const [store, setStore] = React.useState<
      SelectValueModalStore | undefined
    >();
    let options: ZSidLabel[] = [];
    if (value) {
      const v1 = zSidLabel.safeParse(value);
      if (v1.success) {
        options = [v1.data];
      } else {
        const v2 = zSidLabel.array().safeParse(value);
        if (v2.success) {
          options = v2.data;
        }
      }
    }

    const onOpen = () => {
      setStore(
        new SelectValueModalStore(
          component.valuesCatalogId,
          component.labelTemplate,
          multi,
          options,
        ),
      );
    };
    const onClose = () => {
      setStore(undefined);
    };
    const onApply = () => {
      if (store?.data.status === "ready") {
        const { tableStore, makeOption } = store.data.result;
        const opsList = tableStore.selectedKeys.map((key) => ({
          id: String(key),
          label: makeOption(key).label,
        }));
        onChange?.(multi ? opsList : (opsList[0] ?? null));
        onClose();
      }
    };
    const onButtonChange = (newValue: ZSidLabel[] | null) => {
      // предполагается что если доступно редактирование компонента Select, то модалка не открыта и стор отсутствует
      if (!newValue) {
        onChange?.(newValue);
      }
    };

    return (
      <SelectFromModalButton
        disabled={disabled}
        activateCallback={activateCallback}
        options={options}
        onOpen={onOpen}
        onBlur={onBlur}
        onChange={onButtonChange}
      >
        <Modal
          open={!!store}
          onCancel={onClose}
          width="80vw"
          title={store?.title ?? "..."}
          centered
          okText="Выбрать"
          okButtonProps={{ disabled: store?.data.status !== "ready" }}
          onOk={onApply}
        >
          <div className={styles.content}>
            {!!store && (
              <LoaderBox
                remoteData={store.data}
                drawReady={(data) => <Inner data={data} />}
              />
            )}
          </div>
        </Modal>
      </SelectFromModalButton>
    );
  });

type PropsInner = {
  data: Data;
};

const Inner: React.FC<PropsInner> = observer((props) => {
  const { data } = props;
  const { tableStore, columns, makeOption } = data;
  return (
    <ToolBox2
      mainTool={<RtSelection store={tableStore} makeOption={makeOption} />}
    >
      <ToolBox2
        mainTool={<RtSearch store={tableStore} />}
        extraTools={<MdmTableSystemTools store={tableStore} />}
      >
        <MdmTable
          store={tableStore}
          columns={columns}
          rowClick={{
            type: "click",
            handler: (row) => tableStore.changeSelection(row.id, "toggle"),
          }}
        />
      </ToolBox2>
    </ToolBox2>
  );
});
